import React, {
  CSSProperties,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Divider, Form, Input, notification, Row } from 'antd';
import Col, { ColProps as AntColProps } from 'antd/lib/col';
import { FormItemProps } from 'antd/lib/form';
import { isNil, omit, pick } from 'lodash';
import { SaveOutlined, SwitcherOutlined } from '@ant-design/icons';

import useComponentConfiguration from 'DisplayFramework/useComponentConfiguration';
import { useDisplayFrameworkContext } from 'DisplayFramework/hooks/useDisplayFrameworkContext';
import { AuthContext } from './Auth/Auth';
import {
  ParPagComponentRequest,
  updatePageComponent,
} from 'services/displayFramework';
import ModalControllerNV from './ModalControllerNV';
import ComcodeConfigModal from './ComcodeConfigModal';
import ParamListModal from './ParametersListModal';

const COL_SIZE_PROPS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'];

type ColProps =
  | ({ col?: true } & Pick<
      AntColProps,
      'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'
    >)
  | { col?: false };

export type FormInputProps = Omit<
  FormItemProps<any>,
  'label' | 'children' | 'required'
> &
  ColProps & {
    children?: ReactElement;
    /**
     * If not provided, `name` will be used as `comcode`
     */
    comcode?: string;
    fullWidth?: boolean;
    noMargin?: boolean;
    colStyle?: CSSProperties;
    initialValue?: any;
    onlylabel?: string;
    icon?: ReactElement;
    labelstyle?: CSSProperties;
    placeholderprops?: any;
  };

const FormInput = ({
  children = <Input />,
  col,
  hidden,
  comcode,
  initialValue,
  fullWidth = true,
  rules: rulesProp = [],
  style: styleProp = {},
  colStyle = {},
  onlylabel,
  noMargin = false,
  icon,
  labelstyle,
  placeholderprops,
  ...otherProps
}: FormInputProps) => {
  const {
    hint,
    isHidden,
    label,
    message,
    placeholder,
    required,
    isDisabled,
    style,
    styleAsString,
  } = useComponentConfiguration(
    comcode ?? (otherProps.name as string) ?? onlylabel
  );
  const { pagcode, refresh } = useDisplayFrameworkContext();
  const [newLabel, setNewLabel] = useState(null as any);
  const PresetRequest: ParPagComponentRequest = useMemo(
    () => ({
      comcode: comcode ?? (otherProps.name as string) ?? onlylabel ?? '',
      comformat: '',
      comhint: hint ?? '',
      comlabel: newLabel === '' ? label! : newLabel,
      commessage: message ?? '',
      complaceholder: placeholder ?? '',
      comrequired: required,
      comstyle: styleAsString ?? '',
      comtype: '',
      pagcode: pagcode ?? '',
    }),
    [
      comcode,
      otherProps.name,
      onlylabel,
      hint,
      newLabel,
      label,
      message,
      placeholder,
      required,
      styleAsString,
      pagcode,
    ]
  );
  const { isInspect } = useContext(AuthContext);

  useEffect(() => {
    if (isInspect) setNewLabel(label!);
  }, [isInspect]);

  let rules = [...rulesProp];
  if (required) {
    rules.push({ required: true, message });
  }

  let formItemStyle: CSSProperties = {};
  if (noMargin) {
    formItemStyle.margin = 7;
  }

  const handleSaveNewLabel = (request: ParPagComponentRequest) => {
    updatePageComponent({
      request: request,
    }).then((res) => {
      if (res.code === '200') {
        notification.success({
          message: res.message,
          description: res.description,
        });
        refresh();
      } else {
        notification.error({
          message: res.message,
          description: res.description,
        });
      }
    });
  };

  const input = (
    <Form.Item
      label={
        isInspect ? (
          <Input
            value={isNil(newLabel) ? label! : newLabel}
            size="small"
            onChange={(event) => {
              setNewLabel(event.target.value);
            }}
            className="input-inspect"
            addonAfter={
              <>
                <SaveOutlined
                  style={{ color: '#00a896' }}
                  onClick={(event) => {
                    event.preventDefault();
                    handleSaveNewLabel({
                      ...PresetRequest,
                      comlabel: isNil(newLabel) ? label! : newLabel,
                    });
                  }}
                />
                <Divider type="vertical" />
                <ModalControllerNV
                  modal={({ setVisible, visible }) => (
                    <ComcodeConfigModal
                      preset={{ ...PresetRequest, comlabel: label }}
                      onSave={(values) => {
                        setNewLabel('');
                        handleSaveNewLabel({
                          ...PresetRequest,
                          ...values,
                          pagcode: pagcode,
                        });
                      }}
                      onCancel={() => setVisible(false)}
                      visible={visible}
                    />
                  )}
                  content={({ setVisible, visible }) => (
                    <SwitcherOutlined
                      onClick={(event) => {
                        event.preventDefault();
                        setVisible(true);
                      }}
                    />
                  )}
                />
              </>
            }
          />
        ) : (
          <span>
            {icon && <span style={{ marginRight: 10 }}>{icon}</span>}
            <span style={labelstyle ? labelstyle : { fontWeight: 500 }}>
              {label}
            </span>
          </span>
        )
      }
      rules={rules}
      {...omit(otherProps, COL_SIZE_PROPS)}
      style={{
        ...formItemStyle,
        visibility:
          children.props.hidden !== undefined
            ? children.props.hidden
            : isHidden
            ? 'hidden'
            : 'visible',
      }}
      initialValue={initialValue}
    >
      {children.props.parcode && isInspect ? (
        <ModalControllerNV
          modal={({ setVisible, visible }) => (
            <ParamListModal
              onCancel={() => setVisible(false)}
              visible={visible}
              parcode={children.props.parcode}
              title="Parameter List"
            />
          )}
          content={({ setVisible, visible }) => (
            <Row>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  setVisible(true);
                }}
              >
                parcode: &nbsp;<b>{children.props.parcode}</b>
              </a>
            </Row>
          )}
        />
      ) : (
        React.cloneElement(children, {
          placeholder:
            placeholderprops ? placeholderprops : placeholder === undefined || placeholder === '' ? label : placeholder,
          title: hint,
          disabled:
            children.props.disabled !== undefined
              ? children.props.disabled
              : isDisabled,
          style: fullWidth
            ? { width: '100%', ...style, ...styleProp }
            : { ...style, ...styleProp },
        })
      )}
    </Form.Item>
  );

  if (
    (children.props.hidden !== undefined && children.props.hidden) ||
    isHidden
  ) {
    return null;
  }

  if (col) {
    return (
      <Col
        {...pick(otherProps, COL_SIZE_PROPS)}
        style={{
          ...colStyle,
          visibility:
            children.props.hidden !== undefined
              ? children.props.hidden
              : isHidden
              ? 'hidden'
              : 'visible',
        }}
      >
        {input}
      </Col>
    );
  } else {
    return input;
  }
};

export default FormInput;
